/**
 * 优化的API服务
 * 集成缓存、并发控制、错误重试等功能
 */

import axios from 'axios';
import { performanceOptimizer } from '../utils/performanceOptimizer';
import { API_URL, GUEST_TOKEN } from '../config';

class OptimizedApiService {
  constructor() {
    this.baseURL = API_URL; // 添加 baseURL 属性
    this.api = axios.create({
      baseURL: API_URL,
      timeout: 15000, // 增加超时时间
      headers: {
        'Content-Type': 'application/json'
      }
    });

    this.setupInterceptors();
    this.requestQueue = new Map();
    this.maxRetries = 2; // 减少重试次数
    this.retryDelay = 2000; // 增加重试延迟
    
    // 断路器状态
    this.circuitBreaker = {
      failures: 0,
      threshold: 5,
      timeout: 30000, // 30秒
      state: 'CLOSED', // CLOSED, OPEN, HALF_OPEN
      nextAttempt: 0
    };
  }

  setupInterceptors() {
    // 请求拦截器
    this.api.interceptors.request.use(
      (config) => {
        // 添加认证token - 优先使用GUEST_TOKEN确保认证
        config.headers.Authorization = `Bearer ${GUEST_TOKEN}`;
        config.headers['Content-Type'] = 'application/json';

        // 生成请求缓存键
        config.cacheKey = this.generateCacheKey(config);
        
        return config;
      },
      (error) => Promise.reject(error)
    );

    // 响应拦截器
    this.api.interceptors.response.use(
      (response) => {
        // 缓存GET请求的响应
        if (response.config.method === 'get' && response.config.cacheKey) {
          const ttl = this.getCacheTTL(response.config.url);
          performanceOptimizer.cacheResponse(
            response.config.cacheKey,
            response.data,
            ttl
          );
        }
        return response;
      },
      (error) => {
        console.error('API Error:', error);
        return Promise.reject(error);
      }
    );
  }

  /**
   * 生成缓存键
   */
  generateCacheKey(config) {
    const { method, url, params } = config;
    const key = `${method}:${url}:${JSON.stringify(params || {})}`;
    // 使用 encodeURIComponent 处理非 Latin1 字符，然后使用 btoa
    try {
      return btoa(encodeURIComponent(key)).replace(/[^a-zA-Z0-9]/g, '');
    } catch (error) {
      // 如果仍然失败，使用简单的哈希方法
      let hash = 0;
      for (let i = 0; i < key.length; i++) {
        const char = key.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // 转换为32位整数
      }
      return Math.abs(hash).toString(36);
    }
  }

  /**
   * 获取缓存TTL
   */
  getCacheTTL(url) {
    // 根据不同的API endpoint设置不同的缓存时间
    if (url.includes('/articles')) return 5 * 60 * 1000; // 5分钟
    if (url.includes('/weeklies')) return 10 * 60 * 1000; // 10分钟
    if (url.includes('/ideas')) return 2 * 60 * 1000; // 2分钟
    if (url.includes('/categories')) return 30 * 60 * 1000; // 30分钟
    if (url.includes('/global')) return 60 * 60 * 1000; // 1小时
    
    return 5 * 60 * 1000; // 默认5分钟
  }

  /**
   * 带缓存的GET请求
   */
  async get(url, config = {}) {
    const cacheKey = this.generateCacheKey({ method: 'get', url, params: config.params });
    
    // 检查缓存
    const cached = performanceOptimizer.getCachedResponse(cacheKey);
    if (cached && !config.force) {
      return { data: cached };
    }

    return this.retryRequest(() => this.api.get(url, config));
  }

  /**
   * 批量请求
   */
  async batchGet(requests) {
    console.log('🚀 [API] 开始批量请求:', requests.map(r => ({ url: r.url, key: r.key, params: r.params })));
    
    const apiRequests = requests.map(req => {
      // 构建完整的URL
      const fullUrl = req.url.startsWith('http') ? req.url : `${this.baseURL}${req.url}`;
      
      // 构建查询参数 - 手动处理以支持数组参数
      let queryString = '';
      if (req.params) {
        const params = new URLSearchParams();
        Object.entries(req.params).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v));
          } else {
            params.append(key, value);
          }
        });
        queryString = params.toString();
      }
      
      const urlWithParams = queryString ? `${fullUrl}?${queryString}` : fullUrl;
      
      // 特别记录周刊请求
      if (req.key === 'weeklies') {
        console.log('📰 [周刊API] 请求URL:', urlWithParams);
        console.log('📰 [周刊API] 请求参数:', req.params);
      }
      
      return {
        url: urlWithParams,
        options: {},
        cacheKey: this.generateCacheKey({ method: 'get', url: req.url, params: req.params }),
        cacheTTL: this.getCacheTTL(req.url)
      };
    });

    const results = await performanceOptimizer.batchApiRequests(apiRequests);
    

    
    return results.map((result, index) => ({
      ...requests[index],
      data: result.status === 'fulfilled' ? result.value : null,
      error: result.status === 'rejected' ? result.reason : null
    }));
  }

  /**
   * 带重试的请求
   */
  async retryRequest(requestFn, retries = this.maxRetries) {
    // 检查断路器状态
    this.checkCircuitBreaker();
    
    try {
      const result = await requestFn();
      // 请求成功，记录成功状态
      this.recordSuccess();
      return result;
    } catch (error) {
      if (retries > 0 && this.shouldRetry(error)) {
        await this.delay(this.retryDelay * (this.maxRetries - retries + 1)); // 指数退避
        return this.retryRequest(requestFn, retries - 1);
      }
      throw error;
    }
  }

  /**
   * 判断是否应该重试
   */
  shouldRetry(error) {
    // 检查断路器状态
    if (this.circuitBreaker.state === 'OPEN') {
      return false;
    }
    
    // 不重试客户端资源不足错误
    if (error.code === 'ERR_FAILED' || 
        error.code === 'ERR_INSUFFICIENT_RESOURCES' ||
        error.message?.includes('ERR_FAILED') ||
        error.message?.includes('ERR_INSUFFICIENT_RESOURCES')) {
      this.recordFailure();
      return false;
    }
    
    // 不重试4xx客户端错误
    if (error.response && error.response.status >= 400 && error.response.status < 500) {
      return false;
    }
    
    // 只重试网络错误或5xx服务器错误
    const shouldRetry = !error.response || error.response.status >= 500;
    
    if (!shouldRetry) {
      this.recordFailure();
    }
    
    return shouldRetry;
  }

  /**
   * 延迟函数
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * 记录失败
   */
  recordFailure() {
    this.circuitBreaker.failures++;
    if (this.circuitBreaker.failures >= this.circuitBreaker.threshold) {
      this.circuitBreaker.state = 'OPEN';
      this.circuitBreaker.nextAttempt = Date.now() + this.circuitBreaker.timeout;
      console.warn('断路器已打开，暂停API请求', this.circuitBreaker.timeout / 1000, '秒');
    }
  }

  /**
   * 记录成功
   */
  recordSuccess() {
    this.circuitBreaker.failures = 0;
    this.circuitBreaker.state = 'CLOSED';
  }

  /**
    * 检查断路器状态
    */
   checkCircuitBreaker() {
     if (this.circuitBreaker.state === 'OPEN') {
       if (Date.now() >= this.circuitBreaker.nextAttempt) {
         this.circuitBreaker.state = 'HALF_OPEN';
         console.log('断路器进入半开状态，尝试恢复请求');
       } else {
         throw new Error('服务暂时不可用，请稍后重试');
       }
     }
   }

   /**
    * 处理API错误，提供用户友好的错误消息
    */
   handleApiError(error) {
     // 资源不足错误
     if (error.code === 'ERR_FAILED' || 
         error.code === 'ERR_INSUFFICIENT_RESOURCES' ||
         error.message?.includes('ERR_FAILED') ||
         error.message?.includes('ERR_INSUFFICIENT_RESOURCES')) {
       return {
         type: 'RESOURCE_ERROR',
         message: '网络资源不足，请检查网络连接或稍后重试',
         userMessage: '网络连接不稳定，请稍后重试'
       };
     }

     // 网络连接错误
     if (error.code === 'ECONNABORTED' || error.code === 'ENOTFOUND' || error.code === 'ECONNREFUSED') {
       return {
         type: 'NETWORK_ERROR',
         message: '网络连接失败',
         userMessage: '无法连接到服务器，请检查网络连接'
       };
     }

     // 超时错误
     if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
       return {
         type: 'TIMEOUT_ERROR',
         message: '请求超时',
         userMessage: '请求超时，请稍后重试'
       };
     }

     // HTTP状态码错误
     if (error.response) {
       const status = error.response.status;
       if (status >= 500) {
         return {
           type: 'SERVER_ERROR',
           message: `服务器错误 ${status}`,
           userMessage: '服务器暂时不可用，请稍后重试'
         };
       } else if (status >= 400) {
         return {
           type: 'CLIENT_ERROR',
           message: `客户端错误 ${status}`,
           userMessage: '请求出现问题，请刷新页面重试'
         };
       }
     }

     // 默认错误
     return {
       type: 'UNKNOWN_ERROR',
       message: error.message || '未知错误',
       userMessage: '出现未知错误，请刷新页面重试'
     };
   }

  /**
   * 获取首页数据（优化版）
   */
  async getHomePageData() {
    const requests = [
      { 
        url: '/api/articles',
        params: {
          'populate[cover]': '*',
          'populate[category]': '*',
          'sort[0]': 'publishedAt:desc',
          'pagination[pageSize]': 5
        },
        key: 'articles'
      },
      {
        url: '/api/weeklies',
        params: {
          populate: ['cover'],
          sort: ['publishedAt:desc'],
          'pagination[pageSize]': 2
        },
        key: 'weeklies'
      },
      {
        url: '/api/ideas',
        params: {
          populate: ['media', 'authors', 'tags'],
          sort: ['datetime:desc'],
          'pagination[pageSize]': 4
        },
        key: 'ideas'
      },
      {
          url: '/api/private-stashes',
          params: {
            populate: ['cover', 'stashtype'],
            sort: ['createdAt:desc'],
            'pagination[pageSize]': 3
          },
          key: 'stashes'
        }
    ];

    const results = await this.batchGet(requests);
    
    // 处理结果
    const processedData = {};
    results.forEach(result => {
      if (result.data) {
        processedData[result.key] = this.processApiResponse(result.data, result.key);
      } else {
        processedData[result.key] = { data: [], error: result.error };
      }
    });

    // 预加载关键图片
    this.preloadCriticalImages(processedData);

    return processedData;
  }

  /**
   * 处理API响应数据
   */
  processApiResponse(response, type) {
    if (!response?.data || !Array.isArray(response.data)) {
      console.warn(`Invalid ${type} response format:`, response);
      return { data: [], error: 'Invalid response format' };
    }
    
    const processedItems = response.data.map((item) => {
      let processed;
      switch (type) {
        case 'articles':
          processed = this.processArticleItem(item);
          break;
        case 'weeklies':
          processed = this.processWeeklyItem(item);
          break;
        case 'ideas':
          processed = this.processIdeaItem(item);
          break;
        case 'stashes':
          processed = this.processStashItem(item);
          break;
        default:
          console.warn(`Unknown type: ${type}`);
          return null;
      }
      
      return processed;
    }).filter(Boolean);

    return {
      data: processedItems,
      meta: response.meta || {}
    };
  }

  /**
   * 处理文章数据
   */
  processArticleItem(item) {
    const isV4Format = item.attributes !== undefined;
    const data = isV4Format ? item.attributes : item;
    
    let coverUrl = null;
    if (data.cover?.url) {
      coverUrl = data.cover.url;
    } else if (data.cover?.data?.attributes?.url) {
      coverUrl = data.cover.data.attributes.url;
    }

    // 优化图片URL
    if (coverUrl) {
      coverUrl = performanceOptimizer.optimizeImageUrl(coverUrl, {
        width: 400,
        height: 240,
        quality: 85
      });
    }

    return {
      id: item.id,
      documentId: data.documentId || item.id,
      title: data.title,
      description: data.description,
      slug: data.slug,
      publishedAt: data.publishedAt,
      category: this.processCategory(data.category),
      coverUrl: coverUrl
    };
  }

  /**
   * 处理周刊数据
   */
  processWeeklyItem(item) {
    if (!item || typeof item !== 'object') {
      console.warn('Invalid weekly item format:', item);
      return null;
    }
    
    const isV4Format = item.attributes !== undefined;
    const data = isV4Format ? item.attributes : item;
    
    // 处理封面图片URL
    let coverUrl = null;
    
    if (data.cover) {
      // 直接从cover对象获取URL
      if (data.cover.url) {
        coverUrl = data.cover.url;
      } 
      // 从cover.data.attributes获取URL (Strapi v4格式)
      else if (data.cover.data?.attributes?.url) {
        coverUrl = data.cover.data.attributes.url;
      }
      // 从cover.data获取URL
      else if (data.cover.data?.url) {
        coverUrl = data.cover.data.url;
      }
    }
    
    // 优化图片URL
    if (coverUrl) {
      coverUrl = performanceOptimizer.optimizeImageUrl(coverUrl, {
        width: 600,
        height: 360,
        quality: 85
      });
    }

    return {
      id: item.id,
      documentId: data.documentId || item.id,
      title: data.title,
      description: data.description,
      issue: data.issue,
      slug: data.slug,
      publishedAt: data.publishedAt,
      coverUrl: coverUrl
    };
  }

  /**
   * 处理片语数据
   */
  processIdeaItem(item) {
    if (!item || typeof item !== 'object') {
      console.warn('Invalid idea item format:', item);
      return null;
    }
    
    // 直接使用item作为数据源
    const data = item;
    
    // 处理媒体URL
    let mediaUrls = [];
    
    if (data.media) {
      // 处理API直接返回的media数组
      if (Array.isArray(data.media)) {
        mediaUrls = data.media.map(mediaItem => {
          const url = mediaItem.url || mediaItem.formats?.medium?.url || '';
          const fullUrl = url ? (url.startsWith('http') ? url : `${API_URL}${url}`) : '';
          
          return {
            id: mediaItem.id,
            url: fullUrl,
            mime: mediaItem.mime || 'image/jpeg',
            alt: mediaItem.alternativeText || mediaItem.name || ''
          };
        });
      }
      // 处理Strapi格式的media.data（保留兼容性）
      else if (data.media.data) {
        if (Array.isArray(data.media.data)) {
          mediaUrls = data.media.data.map(mediaItem => {
            const url = mediaItem.attributes.url;
            const fullUrl = url ? (url.startsWith('http') ? url : `${API_URL}${url}`) : '';
            
            return {
              id: mediaItem.id,
              url: fullUrl,
              mime: mediaItem.attributes.mime,
              alt: mediaItem.attributes.alternativeText || ''
            };
          });
        } else if (data.media.data) {
          const url = data.media.data.attributes.url;
          const fullUrl = url ? (url.startsWith('http') ? url : `${API_URL}${url}`) : '';
          
          mediaUrls.push({
            id: data.media.data.id,
            url: fullUrl,
            mime: data.media.data.attributes.mime,
            alt: data.media.data.attributes.alternativeText || ''
          });
        }
      }
    }
    
    // 处理作者信息
    let authors = [];
    if (data.authors) {
      if (Array.isArray(data.authors)) {
        authors = data.authors.map(author => ({
          id: author.id,
          name: author.name,
          avatar: author.avatar?.url || null
        }));
      } else if (data.authors.data) {
        authors = data.authors.data.map(author => ({
          id: author.id,
          name: author.attributes.name,
          avatar: author.attributes.avatar?.data?.attributes?.url || null
        }));
      }
    }
    
    // 处理标签信息
    let tags = [];
    if (data.tags) {
      if (Array.isArray(data.tags)) {
        tags = data.tags.map(tag => ({
          id: tag.id,
          name: tag.name,
          slug: tag.slug || tag.name.toLowerCase().replace(/\s+/g, '-')
        }));
      } else if (data.tags.data) {
        tags = data.tags.data.map(tag => ({
          id: tag.id,
          name: tag.attributes.name,
          slug: tag.attributes.slug
        }));
      }
    }
    
    return {
      id: item.id,
      documentId: data.documentId || item.id,
      content: data.content,
      datetime: data.datetime,
      mediaUrls: mediaUrls,
      authors: authors,
      tags: tags
    };
  }

  /**
   * 处理私藏数据
   */
  processStashItem(item) {
    if (!item || typeof item !== 'object') {
      console.warn('Invalid stash item format:', item);
      return null;
    }
    
    // 直接使用item作为数据源
    const data = item;
    
    // 处理封面图片URL
    let coverUrl = '';
    
    if (data.cover) {
      // 处理API直接返回的cover数组
      if (Array.isArray(data.cover) && data.cover.length > 0) {
        const coverItem = data.cover[0];
        const url = coverItem.url || coverItem.formats?.medium?.url || '';
        coverUrl = url ? (url.startsWith('http') ? url : `${API_URL}${url}`) : '';
      }
      // 处理Strapi格式的cover.data（保留兼容性）
      else if (data.cover.data) {
        if (Array.isArray(data.cover.data) && data.cover.data.length > 0) {
          const url = data.cover.data[0].attributes.url;
          coverUrl = url ? (url.startsWith('http') ? url : `${API_URL}${url}`) : '';
        } else if (data.cover.data && data.cover.data.attributes) {
          const url = data.cover.data.attributes.url;
          coverUrl = url ? (url.startsWith('http') ? url : `${API_URL}${url}`) : '';
        }
      }
      // 处理直接的cover对象
      else if (data.cover.url) {
        const url = data.cover.url;
        coverUrl = url ? (url.startsWith('http') ? url : `${API_URL}${url}`) : '';
      }
    }
    
    // 处理私藏类型信息
    let stashtype = null;
    if (data.stashtype) {
      if (data.stashtype.name) {
        stashtype = {
          id: data.stashtype.id,
          name: data.stashtype.name,
          slug: data.stashtype.slug || data.stashtype.name.toLowerCase().replace(/\s+/g, '-')
        };
      } else if (data.stashtype.data && data.stashtype.data.attributes) {
        stashtype = {
          id: data.stashtype.data.id,
          name: data.stashtype.data.attributes.name,
          slug: data.stashtype.data.attributes.slug
        };
      }
    }
    
    return {
      id: item.id,
      documentId: data.documentId || item.documentId || item.id,
      title: data.title,
      description: data.description,
      url: data.url,
      coverUrl: coverUrl,
      stashtype: stashtype,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt
    };
  }

  /**
   * 处理分类数据
   */
  processCategory(category) {
    if (category?.data) {
      const categoryData = category.data.attributes || category.data;
      return {
        id: category.data.id,
        name: categoryData.name,
        slug: categoryData.slug
      };
    } else if (category) {
      return {
        id: category.id,
        name: category.name,
        slug: category.slug
      };
    }
    return null;
  }

  /**
   * 预加载关键图片
   */
  preloadCriticalImages(data) {
    const imageUrls = [];
    
    // 收集首屏图片URL
    if (data.articles?.data) {
      data.articles.data.slice(0, 2).forEach(article => {
        if (article.coverUrl) imageUrls.push(article.coverUrl);
      });
    }
    
    if (data.weeklies?.data && data.weeklies.data[0]?.coverUrl) {
      imageUrls.push(data.weeklies.data[0].coverUrl);
    }

    // 预加载关键图片
    if (imageUrls.length > 0) {
      performanceOptimizer.preloadCriticalImages(imageUrls);
    }
  }

  /**
   * 清除缓存
   */
  clearCache() {
    performanceOptimizer.cache.clear();
  }

  /**
   * 获取性能指标
   */
  getPerformanceMetrics() {
    return performanceOptimizer.getPerformanceMetrics();
  }
}

// 创建全局实例
export const optimizedApiService = new OptimizedApiService();
export default optimizedApiService;